import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [readingMode, setReadingMode] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);

  const comicId = parseInt(id || '1', 10);
  const comic = comics.find((c) => c.id === comicId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">漫画未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === comic.category)?.name || comic.category;
  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 43423漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.createdAt,
    dateModified: comic.updatedAt,
    url: `https://43423mh.com/article/${comic.id}`,
    inLanguage: 'zh-CN',
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '43423漫画网',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}在哪里可以免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在43423漫画网免费在线阅读《${comic.title}》，支持下拉式阅读模式，无需下载或注册即可畅读全部章节。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}多久更新一次？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${comic.title}》${comic.status === '连载中' ? '目前正在连载中，通常每周更新一话' : '已完结，所有章节均可免费阅读'}。最新更新日期为${comic.updatedAt}。`,
        },
      },
      {
        '@type': 'Question',
        name: '43423漫画网有哪些类型的漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '43423漫画网涵盖热血、恋爱、玄幻、搞笑、悬疑、校园、都市、科幻等多种类型漫画，全部免费在线阅读，支持下拉式高清浏览。',
        },
      },
    ],
  };

  if (readingMode) {
    const chapter = comic.chapters[currentChapter];
    const imageCount = chapter?.pageCount || 45;

    return (
      <>
        <ScrollToTop />
        <div className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-background-200/60 px-4 md:px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => setReadingMode(false)}
            className="flex items-center gap-1 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            返回详情
          </button>
          <span className="text-sm font-medium text-foreground-800 truncate max-w-[200px]">
            {comic.title} - {chapter.title}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentChapter(Math.max(0, currentChapter - 1))}
              disabled={currentChapter === 0}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-up-s-line text-lg text-foreground-600"></i>
            </button>
            <button
              onClick={() => setCurrentChapter(Math.min(comic.chapters.length - 1, currentChapter + 1))}
              disabled={currentChapter === comic.chapters.length - 1}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-down-s-line text-lg text-foreground-600"></i>
            </button>
          </div>
        </div>

        <main className="pt-14 pb-10 bg-background-200/50 min-h-screen">
          <div className="max-w-[800px] mx-auto px-4 py-6">
            <p className="text-center text-sm text-foreground-400 mb-4">
              下拉式阅读 - 向下滚动浏览漫画内容
            </p>
            <div className="space-y-4">
              {Array.from({ length: imageCount }).map((_, i) => (
                <div key={i} className="bg-background-100 rounded-lg overflow-hidden">
                  <div className="aspect-[3/4] flex items-center justify-center bg-background-200/50">
                    <div className="text-center">
                      <p className="text-foreground-400 text-sm">第 {i + 1} 页</p>
                      <p className="text-foreground-300 text-xs mt-1">漫画内容区域</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => setCurrentChapter(Math.max(0, currentChapter - 1))}
                disabled={currentChapter === 0}
                className="px-5 py-2.5 text-sm rounded-full bg-background-50 text-foreground-700 border border-background-200/70 hover:border-primary-300 transition-all whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                上一话
              </button>
              <span className="text-sm text-foreground-500">
                {currentChapter + 1} / {comic.chapters.length}
              </span>
              <button
                onClick={() => setCurrentChapter(Math.min(comic.chapters.length - 1, currentChapter + 1))}
                disabled={currentChapter === comic.chapters.length - 1}
                className="px-5 py-2.5 text-sm rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                下一话
              </button>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10 md:pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${categoryName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <div className="mt-6 flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 flex flex-col sm:flex-row lg:flex-col gap-6">
              <div className="w-48 sm:w-44 lg:w-56 rounded-lg overflow-hidden bg-background-100">
                <div className="aspect-[5/7]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} 漫画封面`}
                    title={`${comic.title} - 43423漫画网免费阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setReadingMode(true); setCurrentChapter(0); }}
                  className="w-full px-5 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  开始阅读 第1话
                </button>
                <button
                  onClick={() => { setReadingMode(true); setCurrentChapter(comic.chapters.length - 1); }}
                  className="w-full px-5 py-3 text-sm font-medium rounded-full bg-background-50 text-foreground-700 border border-background-200/70 hover:border-primary-300 transition-all whitespace-nowrap cursor-pointer"
                >
                  阅读最新话
                </button>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>
              <div className="flex items-center flex-wrap gap-2 mt-3">
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-50 text-primary-600 font-medium">{categoryName}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${comic.status === '连载中' ? 'bg-accent-50 text-accent-700' : 'bg-secondary-50 text-secondary-700'}`}>
                  {comic.status}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500">{tag}</span>
                ))}
              </div>

              <div className="flex items-center flex-wrap gap-4 mt-4 text-sm text-foreground-500">
                <span>作者：<strong>{comic.author}</strong></span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-xs text-accent-500"></i>
                  </span>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </span>
                  {formatViews(comic.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-heart-line text-xs"></i>
                  </span>
                  {formatViews(comic.followers)} 收藏
                </span>
              </div>

              <div className="mt-5 p-4 rounded-lg bg-background-100/80">
                <h4 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-semibold text-foreground-800 mb-3">
                  章节列表 <span className="text-foreground-400 font-normal">(共{comic.chapters.length}话)</span>
                </h4>
                <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                  {comic.chapters.map((chapter, index) => (
                    <button
                      key={chapter.id}
                      onClick={() => { setReadingMode(true); setCurrentChapter(index); }}
                      className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer text-left border border-background-200/40"
                    >
                      <span className="text-sm text-foreground-700 truncate">{chapter.title}</span>
                      <span className="text-xs text-foreground-400 flex-shrink-0 ml-3">{chapter.updatedAt}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-12 md:mt-16">
              <h2 className="text-lg font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}